import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark, Bitcoin, ShieldAlert, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { gbp, setPageMeta } from '../lib/shop';

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { paymentMethods, payByBankStatus } = useSite();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState([]);
  const [methodId, setMethodId] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [usdtAck, setUsdtAck] = useState(false);
  const [form, setForm] = useState({
    email: user?.email || '', customer_name: user?.name || '',
    name: '', line1: '', line2: '', city: '', postcode: '', country: 'United Kingdom',
  });

  useEffect(() => {
    setPageMeta('Checkout');
    api.get('/shipping').then((r) => {
      setShipping(r.data);
      if (r.data.length) setMethodId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email, customer_name: f.customer_name || user.name }));
  }, [user]);

  const ship = shipping.find((s) => s.id === methodId);
  const shipCost = ship ? ((ship.free_threshold && subtotal >= ship.free_threshold) ? 0 : ship.price) : 0;
  const discountAmount = useMemo(() => {
    if (!discountInfo) return 0;
    return discountInfo.type === 'percent' ? Math.round(subtotal * discountInfo.value) / 100 : Math.min(discountInfo.value, subtotal);
  }, [discountInfo, subtotal]);
  const total = Math.max(0, subtotal - discountAmount + (shipCost || 0));

  const methods = useMemo(() => {
    const list = [];
    list.push({
      key: 'pay_by_bank', label: 'Pay by Bank', icon: Landmark,
      desc: 'Instant bank-to-bank payment approved in your own banking app.',
      enabled: paymentMethods.some((m) => m.key === 'pay_by_bank'),
      note: payByBankStatus !== 'active' ? 'AWAITING WALLID APPROVAL — this method activates when our Pay by Bank provider goes live.' : '',
    });
    ['crypto_btc', 'crypto_usdt_trc20', 'crypto_usdt_erc20'].forEach((key) => {
      const m = paymentMethods.find((x) => x.key === key);
      if (m) list.push({ key, label: m.label, icon: Bitcoin, desc: `Pay with ${m.label}. Exact amount, address and QR shown after order placement.`, enabled: true });
    });
    const bt = paymentMethods.find((x) => x.key === 'bank_transfer');
    if (bt) list.push({ key: 'bank_transfer', label: 'Manual Bank Transfer', icon: Receipt, desc: bt.note || 'Pay by direct bank transfer. Account details and your unique payment reference are shown after you place the order.', enabled: true });
    const st = paymentMethods.find((x) => x.key === 'stripe');
    if (st) list.push({ key: 'stripe', label: 'Card Payment', icon: CreditCard, desc: st.note || 'Secure card payment via Stripe. You will be redirected to Stripe to complete payment.', enabled: true });
    return list;
  }, [paymentMethods, payByBankStatus]);

  const isUsdt = payMethod === 'crypto_usdt_trc20' || payMethod === 'crypto_usdt_erc20';

  const applyDiscount = async () => {
    try {
      const { data } = await api.get('/discounts/validate', { params: { code: discount, subtotal } });
      setDiscountInfo(data);
      toast.success(`Code ${data.code} applied`);
    } catch (e) { setDiscountInfo(null); toast.error(errMsg(e)); }
  };

  const placeOrder = async () => {
    if (!items.length) return;
    if (!payMethod) { toast.error('Choose a payment method'); return; }
    if (isUsdt && !usdtAck) { toast.error('Please acknowledge the network warning'); return; }
    setPlacing(true);
    try {
      const { data } = await api.post('/orders', {
        items: items.map((i) => ({ product_id: i.id, variant_id: i.variant_id || '', quantity: i.quantity })),
        email: form.email, customer_name: form.customer_name,
        shipping_address: { name: form.name || form.customer_name, line1: form.line1, line2: form.line2, city: form.city, postcode: form.postcode, country: form.country },
        shipping_method_id: methodId, discount_code: discountInfo?.code || '', payment_method: payMethod,
      });
      clear();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      navigate(`/order/${data.order.id}`);
    } catch (e) {
      const msg = errMsg(e);
      if (String(msg).includes('AWAITING_WALLID_APPROVAL')) {
        toast.error('Pay by Bank is awaiting provider approval. Please choose another method or contact us.');
      } else toast.error(msg);
    } finally { setPlacing(false); }
  };

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="checkout-empty">
        <p className="font-display font-bold uppercase text-2xl">Your basket is empty</p>
        <button onClick={() => navigate('/products')} className="mt-6 bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-xs uppercase tracking-widest">Browse catalogue</button>
      </div>
    );
  }

  const inp = "w-full border hairline px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0B0F12]";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="checkout-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Secure checkout</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-8">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">
          <section className="border hairline bg-white p-6">
            <p className="font-display font-bold uppercase text-xl mb-4">1 · Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="checkout-email" type="email" required placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} />
              <input data-testid="checkout-name" placeholder="Full name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inp} />
            </div>
            {!user && <p className="text-[11px] text-[#8A8F95] mt-3">Checking out as guest. <button onClick={() => navigate('/login')} className="underline">Sign in</button> to track orders in your account.</p>}
          </section>

          <section className="border hairline bg-white p-6">
            <p className="font-display font-bold uppercase text-xl mb-4">2 · Delivery address</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="address-name" placeholder="Recipient name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
              <input data-testid="address-postcode" placeholder="Postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className={inp} />
              <input data-testid="address-line1" placeholder="Address line 1" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className={`${inp} sm:col-span-2`} />
              <input data-testid="address-line2" placeholder="Address line 2 (optional)" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} className={inp} />
              <input data-testid="address-city" placeholder="Town / City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} />
            </div>
          </section>

          <section className="border hairline bg-white p-6">
            <p className="font-display font-bold uppercase text-xl mb-4">3 · Shipping</p>
            <div className="space-y-2">
              {shipping.map((s) => (
                <label key={s.id} data-testid={`shipping-${s.id}`} className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${methodId === s.id ? 'border-[#0B0F12] bg-[#FAFAF8]' : 'hairline hover:border-[#0B0F12]'}`}>
                  <input type="radio" name="shipping" checked={methodId === s.id} onChange={() => setMethodId(s.id)} className="accent-[#0B0F12]" />
                  <div className="flex-1">
                    <p className="font-display font-bold uppercase">{s.name}</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95]">{s.estimated_delivery}{s.tracked ? ' · Tracked' : ''}{s.signature ? ' · Signature' : ''}</p>
                  </div>
                  <span className="font-mono2 text-sm font-semibold">
                    {(s.free_threshold && subtotal >= s.free_threshold) ? 'FREE' : gbp(s.price)}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="border hairline bg-white p-6">
            <p className="font-display font-bold uppercase text-xl mb-4">4 · Payment</p>
            <div className="space-y-2">
              {methods.map((m) => (
                <div key={m.key}>
                  <label data-testid={`paymethod-${m.key}`}
                    className={`flex items-center gap-3 border p-4 transition-colors ${m.enabled ? 'cursor-pointer' : 'opacity-70'} ${payMethod === m.key ? 'border-[#0B0F12] bg-[#FAFAF8]' : 'hairline'} ${m.enabled ? 'hover:border-[#0B0F12]' : ''}`}>
                    <input type="radio" name="paymethod" disabled={!m.enabled} checked={payMethod === m.key} onChange={() => setPayMethod(m.key)} className="accent-[#0B0F12]" />
                    <m.icon className="w-5 h-5" />
                    <div className="flex-1">
                      <p className="font-display font-bold uppercase">{m.label}</p>
                      <p className="text-[12px] text-[#575B60]">{m.desc}</p>
                      {m.note && <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#D97706] mt-1">{m.note}</p>}
                    </div>
                    {!m.enabled && <span className="font-mono2 text-[9px] uppercase tracking-widest border border-[#D8D8D0] px-2 py-1 text-[#8A8F95]">Not live</span>}
                  </label>
                  {isUsdt && payMethod === m.key && (
                    <label className="flex items-start gap-3 bg-[#FFF7ED] border border-[#FDE68A] p-4 mt-2 cursor-pointer" data-testid="usdt-warning">
                      <input type="checkbox" checked={usdtAck} onChange={(e) => setUsdtAck(e.target.checked)} className="mt-0.5 accent-[#D97706]" data-testid="usdt-ack" />
                      <span className="text-[12px] text-[#92400E] leading-relaxed">
                        <strong>Network warning:</strong> Only send USDT using the selected network ({m.label}). Transfers using an incorrect or unsupported network may not be recoverable.
                      </span>
                    </label>
                  )}
                </div>
              ))}
              {methods.filter((m) => m.enabled).length === 0 && (
                <p className="text-sm text-[#575B60] border hairline p-4">No payment methods are currently live. Pay by Bank is awaiting provider approval. Please contact orders@vqhealth.co.uk to complete your order.</p>
              )}
            </div>
          </section>
        </div>

        <aside className="border hairline bg-white p-6 h-fit lg:sticky lg:top-32">
          <p className="font-display font-bold uppercase text-xl mb-4">Order summary</p>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((it) => (
              <div key={it.id} className="flex gap-3">
                <img src={it.image} alt="" className="w-12 h-12 object-cover bg-[#F4F4F0]" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase text-sm leading-tight truncate">{it.name}</p>
                  <p className="font-mono2 text-[10px] text-[#8A8F95]">{it.strength} × {it.quantity}</p>
                </div>
                <span className="font-mono2 text-sm">{gbp(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <input data-testid="discount-input" placeholder="Discount code" value={discount} onChange={(e) => setDiscount(e.target.value)}
              className="flex-1 border hairline px-3 py-2.5 text-sm font-mono2 uppercase focus:outline-none focus:border-[#0B0F12]" />
            <button data-testid="discount-apply" onClick={applyDiscount} className="border border-[#0B0F12] px-4 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#F4F4F0]">Apply</button>
          </div>
          <div className="mt-4 space-y-2 font-mono2 text-sm border-t hairline pt-4">
            <div className="flex justify-between text-[#575B60]"><span>Subtotal</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between text-[#575B60]"><span>Shipping</span><span>{ship ? (shipCost === 0 ? 'FREE' : gbp(shipCost)) : '—'}</span></div>
            {discountAmount > 0 && <div className="flex justify-between text-[#1F8A58]"><span>Discount ({discountInfo.code})</span><span>−{gbp(discountAmount)}</span></div>}
            <div className="flex justify-between font-bold text-lg border-t hairline pt-2"><span>Total</span><span data-testid="checkout-total">{gbp(total)}</span></div>
          </div>
          <button data-testid="place-order-button" onClick={placeOrder} disabled={placing || !payMethod}
            className="mt-5 w-full bg-[#0B0F12] text-white py-4 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors disabled:opacity-50">
            {placing ? 'Placing order…' : 'Place order'}
          </button>
          <p className="flex items-start gap-2 text-[11px] text-[#8A8F95] mt-4 leading-relaxed">
            <ShieldAlert className="w-4 h-4 shrink-0" /> All products are for in-vitro laboratory research only. Not for human or veterinary use.
          </p>
        </aside>
      </div>
    </div>
  );
}
